/**
 * CamposNumericos — a família de campos numéricos do app (dinheiro, percentual,
 * medida). Fonte única: nenhuma tela monta o seu.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║  ATENÇÃO: NÃO É NATIVO DO ADMINUX. É CRIAÇÃO NOSSA, irmã da "Data Rápida" ║
 * ║  (`CustomCamposData.tsx`). Se um campo de valor se comportar de forma estranha, ║
 * ║  ESTE arquivo é o primeiro suspeito — não o tema.                         ║
 * ║  Doc: .claude/docs/tema/11-campos-valor-rapido.md                         ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 *
 * ┌─ COMO ISTO É ORGANIZADO ──────────────────────────────────────────────────┐
 * │ Há UM campo de verdade — `CustomCampoNumero` — com todas as propriedades.  │
 * │ Os outros três NÃO têm lógica própria: são o mesmo campo com alguns        │
 * │ padrões já preenchidos. Pense em classe base e subclasses.                 │
 * │                                                                            │
 * │   CustomCampoNumero            base configurável (casas, símbolo, teto…)   │
 * │   ├─ CustomCampoNumeroMoeda        = base + símbolo "R$"  + 2 casas        │
 * │   ├─ CustomCampoNumeroPercentual   = base + sufixo  "%"   + 0 casas        │
 * │   └─ CustomCampoNumeroMedida       = base + sufixo livre ("kg", "m", "un") │
 * │                                                                            │
 * │ O prefixo `Custom` marca componente criação nossa — regra do hub, em       │
 * │ `.claude-hub/convencao-nomenclatura-componentes.md`.                       │
 * │                                                                            │
 * │ TODO padrão é sobrescrevível na chamada: `casas={6}` dá um campo de preço  │
 * │ com seis decimais e `moeda="US$"` dá um campo em dólar. Não é preciso      │
 * │ mexer neste arquivo para variar um campo.                                  │
 * │                                                                            │
 * │ A lógica (conversão, formatação, as bordas doBanco/paraBanco/paraConta)    │
 * │ mora em `lib/numero.ts` — sem React, testável no Node.                     │
 * └────────────────────────────────────────────────────────────────────────────┘
 *
 * A DIGITAÇÃO ("Valor Rápido"): o campo não tem separador para digitar. Só
 * dígitos entram; a vírgula e os pontos de milhar são DESENHADOS pelo campo,
 * sempre no lugar certo, e o número cresce da DIREITA para a ESQUERDA:
 *     digita 8   → 0,08
 *     digita 0   → 0,80
 *     digita 6   → 8,06
 *     digita 6   → 80,66
 * `.` e `,` digitados são IGNORADOS — é justamente essa tecla que causa
 * confusão entre teclados, então ela deixa de significar alguma coisa.
 * Backspace desfaz na mesma lógica, empurrando de volta para a direita.
 *
 * QUANDO DESLIGAR (`rapida={false}`): a digitação rápida pressupõe um número de
 * casas FIXO. Onde a precisão varia de linha para linha — uma medida que às
 * vezes é 1,5 e às vezes 1,25 — o modo clássico (vírgula digitada, com o parser
 * corrigido) é mais honesto. Fora isso, deixe ligada. O interruptor global
 * `VALOR_RAPIDO` (em `lib/numero.ts`) desliga o app inteiro de uma vez.
 */
import React, { useRef, useState } from 'react';
import {
  MAX_DIGITOS,
  SEPARADOR_PADRAO,
  VALOR_RAPIDO,
  avisarValorEstranho,
  canonicoParaDigitos,
  digitosParaCanonico,
  formatarDigitos,
  paraConta,
  parseTextoLivre,
  semZerosAEsquerda,
} from '@/lib/numero';
import type { Separadores } from '@/lib/numero';

export type PropsValor = {
  /** `''` (vazio) | canônico com ponto decimal: `'80.66'`, `'-12.50'`. */
  valor: string;
  onChange: (canonico: string) => void;
  /** Casas decimais do campo. 2 no dinheiro, 0 no percentual, 6 num preço
   *  unitário que dá dízima — o que a tela precisar. */
  casas?: number;
  /** Símbolo à ESQUERDA. `true` = "R$"; string = o símbolo que você quiser
   *  ("US$", "€"); ausente/false = sem símbolo. */
  moeda?: boolean | string;
  /** Texto à DIREITA — "%", "kg", "m", "un". */
  sufixo?: string;
  /** Aceita valor negativo (tecla `-` alterna o sinal). Padrão: não. */
  negativo?: boolean;
  /** Teto opcional: a tecla que passaria disso é RECUSADA. Não vem ligado em
   *  lugar nenhum — só quem tem limite real (desconto ≤ 100 %) o declara. */
  maximo?: number;
  /** Separadores de exibição. Padrão pt-BR (vírgula decimal, ponto no milhar). */
  separadores?: Separadores;
  /** Rótulo flutuante DENTRO da caixa. Sem ele, use `<label>` por fora. */
  label?: string;
  id?: string;
  disabled?: boolean;
  /** Campo compacto (`form-control-sm`), para barra de filtro. */
  sm?: boolean;
  /** Campo grande (`form-control-lg`) — o padrão das telas de celular. */
  lg?: boolean;
  /** Largura fixa do campo inteiro (`'6rem'`, `120`), para linha com vários
   *  controles lado a lado — quantidade ao lado de descrição, por exemplo. Vai
   *  no `input-group`, e não no `<input>`, senão o addon (R$, %) fica de fora
   *  da conta e o conjunto passa da largura pedida. */
  largura?: string | number;
  /** Texto quando vazio. Padrão: `0,00` conforme as casas. */
  placeholder?: string;
  /** Alinhar o número à direita (padrão) — reforça que ele cresce para a esquerda. */
  alinharDireita?: boolean;
  /** Pinta o número de vermelho quando negativo. Ligado por padrão nos campos
   *  que aceitam negativo — dinheiro que sai tem que dar na vista. */
  corNegativo?: boolean;
  autoFocus?: boolean;
  onBlur?: () => void;
  /** Desliga a digitação rápida só neste campo (volta ao texto livre). */
  rapida?: boolean;
};

const classes = (p: PropsValor, negativo = false) =>
  `form-control${p.sm ? ' form-control-sm' : ''}${p.lg ? ' form-control-lg' : ''}${
    p.alinharDireita === false ? '' : ' text-end'
  }${negativo && p.corNegativo !== false ? ' text-danger fw-semibold' : ''}`;

/** Símbolo da esquerda: `true` vira "R$", string vale como veio. */
const simbolo = (moeda: PropsValor['moeda']): string | null =>
  moeda === true ? 'R$' : typeof moeda === 'string' && moeda ? moeda : null;

/** Embrulho comum: input-group com símbolo/sufixo e rótulo flutuante opcionais. */
function Moldura({
  moeda,
  sufixo,
  label,
  id,
  sm,
  lg,
  largura,
  children,
}: PropsValor & { children: React.ReactElement }) {
  const esq = simbolo(moeda);
  const grupo = `input-group${sm ? ' input-group-sm' : ''}${lg ? ' input-group-lg' : ''}`;
  const estilo = largura ? { width: largura, flex: '0 0 auto' } : undefined;
  const campo = label ? (
    // .form-floating DENTRO do .input-group é o arranjo que o Bootstrap 5
    // suporta para casar rótulo flutuante com addon — mesmo padrão do CustomCamposData.
    <div className="form-floating">
      {children}
      <label htmlFor={id}>{label}</label>
    </div>
  ) : (
    children
  );

  if (!esq && !sufixo) return <div className={grupo} style={estilo}>{campo}</div>;
  return (
    <div className={grupo} style={estilo}>
      {esq && <span className="input-group-text">{esq}</span>}
      {campo}
      {sufixo && <span className="input-group-text">{sufixo}</span>}
    </div>
  );
}

// ============================================================ digitação rápida

function CampoRapido(p: PropsValor) {
  const casas = p.casas ?? 2;
  const sep = p.separadores ?? SEPARADOR_PADRAO;
  const ref = useRef<HTMLInputElement>(null);
  /** O foco veio do mouse? (mousedown chega antes do focus) — decide entre
   *  "selecionar tudo" (chegou pelo Tab) e "caret no fim" (clicou). */
  const porMouse = useRef(false);

  // Quase nada de state interno, de propósito: tudo é derivado do `valor`
  // controlado, então o campo NUNCA discorda do que o pai tem. A ÚNICA exceção
  // é o sinal com o campo ainda vazio: "menos" digitado antes do primeiro
  // dígito não tem como caber no canônico ('' não guarda sinal, e '-0.00'
  // seria mentira para o pai). Ele vive aqui até o primeiro dígito entrar.
  const [sinalPendente, setSinalPendente] = useState(false);

  const bruto = String(p.valor ?? '').trim();
  const digitos = canonicoParaDigitos(bruto, casas, p.id ?? p.label);
  const negativo = !!p.negativo && (bruto.startsWith('-') || (!digitos && sinalPendente));

  // Com o campo vazio e o sinal pendente, o "−" precisa aparecer sozinho: é o
  // retorno visual de que o próximo número já sai negativo.
  const texto = digitos ? formatarDigitos(digitos, casas, negativo, sep) : negativo ? '-' : '';

  /** O caret vive no FIM. É o que garante que o dígito novo entre pela direita
   *  mesmo se o mouse tiver parado no meio do número. */
  const aoFim = () => {
    const el = ref.current;
    if (!el) return;
    const n = el.value.length;
    el.setSelectionRange(n, n);
  };

  const aplicar = (novosDigitos: string, novoNegativo: boolean) => {
    const d = semZerosAEsquerda(novosDigitos).slice(0, MAX_DIGITOS);
    // Teto, quando o campo declarou um: a tecla que passaria do limite é
    // RECUSADA, e não corrigida para o máximo — corrigir apagaria o que a pessoa
    // já digitou e ela não veria por quê. Recusar é a tecla não existir.
    if (p.maximo != null && d && Math.abs(paraConta(digitosParaCanonico(d, casas))) > p.maximo) {
      return;
    }
    setSinalPendente(!d && novoNegativo);
    p.onChange(digitosParaCanonico(d, casas, novoNegativo));
    requestAnimationFrame(aoFim);
  };

  /**
   * A digitação é lida do TEXTO resultante, não da tecla — assim funciona igual
   * no teclado físico, no teclado do celular (onde `key` costuma vir como
   * "Unidentified") e ao colar. Como só os dígitos do texto importam e o caret
   * está sempre no fim, "digitar" acrescenta na direita e "Backspace" tira da
   * direita, sozinhos, sem contas de posição de cursor.
   */
  const aoDigitar = (e: React.ChangeEvent<HTMLInputElement>) => {
    const t = e.target.value;
    const antes = texto.replace(/\D/g, '');
    const agora = t.replace(/\D/g, '');
    // Apagar até sobrar só zero ESVAZIA o campo. Sem isto, "0,00" viraria um
    // poço: o Backspace tiraria um zero e a formatação o devolveria na hora.
    const zerando = agora.length < antes.length && /^0*$/.test(agora);
    aplicar(zerando ? '' : agora, !!p.negativo && t.includes('-'));
  };

  const aoTeclar = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // A tecla da confusão: ponto e vírgula não fazem NADA aqui. O separador é
    // desenhado pelo campo, então não há posição errada possível.
    if (e.key === '.' || e.key === ',') {
      e.preventDefault();
      return;
    }
    // "menos" alterna o sinal a QUALQUER momento — inclusive com o campo ainda
    // vazio, que é como se escreve um número negativo naturalmente ("-12,50",
    // e não "12,50" e depois o sinal). "mais" volta para positivo.
    if (e.key === '-' || e.key === '+') {
      e.preventDefault();
      if (p.negativo) aplicar(digitos, e.key === '-' ? !negativo : false);
      return;
    }
  };

  const aoColar = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const colado = e.clipboardData.getData('text');
    // Colado COM separador é um número escrito ("1.234,56" / "80.66") — vale o
    // que está escrito. Colado só com dígitos segue a regra do campo (centavos).
    if (!/[.,]/.test(colado)) return;
    e.preventDefault();
    const n = parseTextoLivre(colado);
    if (n === null) {
      avisarValorEstranho({ motivo: 'colado não parece número', recebido: colado, campo: p.id });
      return;
    }
    aplicar(canonicoParaDigitos(Math.abs(n).toFixed(casas), casas), !!p.negativo && n < 0);
  };

  return (
    <Moldura {...p}>
      <input
        ref={ref}
        id={p.id}
        type="text"
        className={classes(p, negativo)}
        value={texto}
        disabled={p.disabled}
        autoFocus={p.autoFocus}
        onChange={aoDigitar}
        onKeyDown={aoTeclar}
        onPaste={aoColar}
        onMouseDown={() => {
          porMouse.current = true;
        }}
        // Chegou pelo Tab/Shift+Tab: SELECIONA TUDO, como faz qualquer campo de
        // texto — voltar num campo para refazer o número é o caso comum, e aí o
        // primeiro dígito já substitui o valor inteiro. Chegou pelo clique: o
        // caret vai para o fim, porque quem clicou quer emendar, não refazer.
        onFocus={(e) => {
          const el = e.currentTarget;
          const clique = porMouse.current;
          porMouse.current = false;
          requestAnimationFrame(() => (clique ? aoFim() : el.select()));
        }}
        onClick={() => {
          const el = ref.current;
          // não atrapalha quem selecionou de propósito (para copiar)
          if (el && el.selectionStart === el.selectionEnd) aoFim();
        }}
        onBlur={p.onBlur}
        // "numeric" e não "decimal": o campo IGNORA vírgula, então oferecer a
        // tecla dela no celular seria mentir para quem digita.
        inputMode="numeric"
        // com rótulo flutuante o placeholder tem que ser um espaço (o Bootstrap
        // usa :placeholder-shown para decidir a posição do rótulo)
        placeholder={p.label ? ' ' : (p.placeholder ?? formatarDigitos('0', casas, false, sep))}
        autoComplete="off"
        // Cofre de senha põe o ícone dele no canto e atrapalha quem digita.
        // Acontece porque o campo é `type="text"` (é o que permite desenhar a
        // vírgula) e, para o cofre, todo input de texto é candidato a login.
        // `autocomplete="off"` não basta — eles ignoram por política. Um
        // atributo por marca é o que funciona. Mesma solução do CustomCamposData.
        data-form-type="other" /* Dashlane */
        data-lpignore="true" /* LastPass */
        data-1p-ignore="true" /* 1Password */
        data-bwignore="true" /* Bitwarden */
      />
    </Moldura>
  );
}

// ============================================================ modo clássico
// Texto livre — mas com o parser CORRIGIDO (ponto deixa de virar milhar cego).
// É a rota de fuga: `rapida={false}` ou o interruptor global devolvem este
// comportamento sem perder o conserto.

function CampoClassico(p: PropsValor) {
  const casas = p.casas ?? 2;
  const sep = p.separadores ?? SEPARADOR_PADRAO;
  const [rascunho, setRascunho] = useState<string | null>(null);

  const negativoAtual = String(p.valor ?? '').startsWith('-');
  const texto =
    rascunho ??
    formatarDigitos(canonicoParaDigitos(p.valor, casas, p.id), casas, negativoAtual, sep);

  const confirmar = (t: string) => {
    setRascunho(null);
    const n = parseTextoLivre(t);
    if (n === null) {
      if (t.trim()) {
        avisarValorEstranho({ motivo: 'texto não parece número', recebido: t, campo: p.id });
      }
      p.onChange('');
      return;
    }
    const bruto = p.negativo ? n : Math.abs(n);
    // No modo clássico o texto só é lido no Tab/Enter, então aqui não dá para
    // "recusar a tecla": o jeito honesto é aparar no teto.
    const limitado = p.maximo != null ? Math.max(-p.maximo, Math.min(p.maximo, bruto)) : bruto;
    p.onChange(limitado.toFixed(casas));
  };

  return (
    <Moldura {...p}>
      <input
        id={p.id}
        type="text"
        className={classes(p, negativoAtual)}
        value={texto}
        disabled={p.disabled}
        autoFocus={p.autoFocus}
        onChange={(e) => setRascunho(e.target.value)}
        onBlur={(e) => {
          confirmar(e.target.value);
          p.onBlur?.();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            confirmar((e.target as HTMLInputElement).value);
          }
        }}
        inputMode="decimal"
        placeholder={p.label ? ' ' : (p.placeholder ?? formatarDigitos('0', casas, false, sep))}
        autoComplete="off"
        // Cofre de senha põe o ícone dele no canto e atrapalha quem digita.
        // Acontece porque o campo é `type="text"` (é o que permite desenhar a
        // vírgula) e, para o cofre, todo input de texto é candidato a login.
        // `autocomplete="off"` não basta — eles ignoram por política. Um
        // atributo por marca é o que funciona. Mesma solução do CustomCamposData.
        data-form-type="other" /* Dashlane */
        data-lpignore="true" /* LastPass */
        data-1p-ignore="true" /* 1Password */
        data-bwignore="true" /* Bitwarden */
      />
    </Moldura>
  );
}

// ============================================================ os campos
// UM campo de verdade e três atalhos. Os atalhos só preenchem padrões — nenhum
// deles tem regra própria, e tudo neles pode ser sobrescrito na chamada.

/**
 * BASE — o campo genérico. Use direto quando nenhum atalho servir.
 *
 *   <CustomCampoNumero casas={0} valor={qtd} onChange={setQtd} />
 *   <CustomCampoNumero rapida={false} casas={3} valor={medida} onChange={setMedida} />
 */
export function CustomCampoNumero({ rapida = VALOR_RAPIDO, ...p }: PropsValor) {
  return rapida ? <CampoRapido {...p} /> : <CampoClassico {...p} />;
}

/**
 * DINHEIRO — base + símbolo à esquerda + 2 casas.
 *
 *   <CustomCampoNumeroMoeda valor={v} onChange={setV} />                 // R$ 1.234,56
 *   <CustomCampoNumeroMoeda moeda="US$" valor={v} onChange={setV} />     // outra moeda
 *   <CustomCampoNumeroMoeda casas={6} valor={v} onChange={setV} />       // preço unitário
 *   <CustomCampoNumeroMoeda negativo valor={v} onChange={setV} />        // negativo em vermelho
 */
export function CustomCampoNumeroMoeda({ moeda = true, casas = 2, ...p }: PropsValor) {
  return <CustomCampoNumero {...p} moeda={moeda} casas={casas} />;
}

/**
 * PERCENTUAL — base + "%" à direita + 0 casas, SEM teto.
 *
 * Zero casas porque percentual aqui é inteiro (5 % do vendedor, 40 % do
 * instalador): `40` digita 40 %, e não 0,40 %. Fração: `casas={1}` → `405` vira
 * 40,5 %. Sem teto porque 100 % não é limite universal (acréscimo de 120 %
 * existe); quem tem limite real declara `maximo={100}`.
 */
export function CustomCampoNumeroPercentual({ sufixo = '%', casas = 0, ...p }: PropsValor) {
  return <CustomCampoNumero {...p} sufixo={sufixo} casas={casas} />;
}

/**
 * MEDIDA — base + unidade à direita ("kg", "m", "cm", "un").
 *
 *   <CustomCampoNumeroMedida unidade="m" casas={2} valor={largura} onChange={setLargura} />
 *   <CustomCampoNumeroMedida unidade="un" casas={0} valor={qtd} onChange={setQtd} />
 *
 * Quando a precisão variar de linha para linha, passe `rapida={false}`: aí a
 * vírgula volta a ser digitada, que é o honesto onde não há casas fixas.
 */
export function CustomCampoNumeroMedida({ unidade, casas = 2, ...p }: PropsValor & { unidade: string }) {
  return <CustomCampoNumero {...p} sufixo={unidade} casas={casas} />;
}
